/**
 * First we will load all of this project's JavaScript dependencies which
 * includes Vue and other libraries. It is a great starting point when
 * building robust, powerful web applications using Vue and Laravel.
 */

require('./bootstrap');

require('./components/homepage-slider');
require('./components/context-slider');
require('./components/video-player');
require('./components/inline-slider');
require('./components/newsletter-form');
require('./components/popup');
require('./components/story-component');

window.transitionVars = {
    imageEnter: 0.7,
    imageEnterDuration: 1.6,
};

/**
 * We load the Greensock libraries in the application scope, since
 * the bootstrap scope is not working for these includes.
 */

import { TweenMax } from 'gsap';
import CustomEase from './plugins/CustomEase';
import Router from './router';

import Preload from 'preload-it';
import {Howl, Howler} from 'howler';

window.App = new Vue({
    el: '#app',
    delimiters: ['${', '}'],

    data() {
        return {
            // Application state
            mobileState: null,
            loading: true,
            showIntroduction: false,
            section: null,
            navigationOpen: false,
            darkHeader: false,
            headerNavigationState: false,

            // Application classes
            router: null,

            // External libraries
            lethargy: null,
            preload: new Preload(),
            soundtrack: null
        };
    },

    created() {
        // Light up navigation.
        this.router = new Router(this);

        // Initialize application section.
        const section = document.head.querySelector('meta[name="initial-section"]');

        if (section) {
            this.section = section.content;
            document.documentElement.classList.add(this.section);
        }
    },

    beforeMount() {
        // Initialize header state.
        this.darkHeader = (this.$el.attributes['data-initial-header'].value === '1');
    },

    mounted() {
        this.initPage();
        EventBus.$on('page-transition-enter-complete', this.initPage);

        // Check for window resize
        window.addEventListener('resize', this.onWindowResize);

        this.handleWindowResize();
        EventBus.$on('window-resize', this.handleWindowResize);

        // Initialize the home sequence if we enter on homepage.
        if (this.section === 'home') {
            this.showIntroduction = true;
            Vue.nextTick(this.initIntroduction);

            return;
        }

        // Default initialisation.
        this.loading = false;
        this.initSoundtrack(false);
        EventBus.$emit('page-init-complete');
    },

    methods: {
        handleWindowResize() {
            // Fix iOS Navigation.
            this.fixiOSNavigation();

            // Check if we are in a mobile state.
            this.mobileState = !window.matchMedia('(min-width: 768px)').matches;

            if (this.mobileState) {
                if (this.soundtrack) {
                    this.soundtrack.pause();
                }
            }
        },

        fixiOSNavigation() {
            if (Modernizr.iphone) {
                TweenMax.set('nav', { height: window.innerHeight });
            }
        },

        initPage() {
            // Help IE users by initializing object-fit polyfill
            Vue.nextTick(() => {
                if (typeof objectFitPolyfill !== 'undefined') {
                    objectFitPolyfill();
                }

                this.initPageScrollController();
            });
        },

        toggleSoundtrack() {
            if (!this.soundtrack) {
                return;
            }

            if (!this.soundtrack.playing()) {
                this.soundtrack.play();

                return;
            }

            this.soundtrack.pause();
        },

        initIntroduction() {
            TweenMax.set(['.start-page .inner .logo', 'header .logo', '#hamburger'], { autoAlpha: 0 });
            TweenMax.set(['.slider-control .circle'], { scale: 0 });
            TweenMax.set(['.slider-control.next .arrow'], { autoAlpha: 0, y: '-25%' });
            TweenMax.set(['.slider-control.previous .arrow'], { autoAlpha: 0, y: '25%' });
            TweenMax.set('.slide .subline .movement-container', { y: '100%' });
            TweenMax.set('.slide .scroll .movement-container', { y: '-100%' });
            TweenMax.set('.slide h1 .movement-container', { y: '100%' });
            TweenMax.set('.logo.clone', { autoAlpha: 0 });

            this.setupLogo();
            EventBus.$on('window-resize', this.setupLogo);

            const tl = new TimelineMax({ paused: true, delay: 1 });
            tl.to('.logo.clone', 0.6, { autoAlpha: 1 });
            tl.to('.start-page', 0, { className: '-=animate-in' });

            const videoUrl = this.$refs['intro-video'].dataset.source;
            this.preload.fetch([
                videoUrl
            ]);

            this.preload.onprogress = event => {
                this.$refs.preloadValue.textContent = event.progress + '%';
            }

            this.preload.oncomplete = items => {
                this.$refs['intro-video'].src = this.preload.getItemByUrl(videoUrl).blobUrl;
                this.$refs['intro-video'].play();

                TweenMax.to('.preload-holder', 0.6, {autoAlpha: 0});
                TweenMax.to('.fullscreen-video-holder', 1, {autoAlpha: 1});
                this.initSoundtrack(true);

                Vue.nextTick(() => {
                    this.loading = false;
                    EventBus.$emit('page-init-complete');
    
                    tl.play();
                });
            }
        },

        initSoundtrack(autoplay) {
            var audioTrack = document.head.querySelector('meta[name=soundtrack-asset]');

            if (!audioTrack) {
                return;
            }

            if (this.mobileState) {
                autoplay = false;
            }

            this.soundtrack = new Howl({
                src: [audioTrack.content],
                autoplay: autoplay,
                loop: true,
                volume: 1
            });
        },

        setupLogo() {
            const logoClone = document.querySelector('.logo.clone');
            const first = document.querySelector('.start-page .inner .logo').getBoundingClientRect();
            const last = document.querySelector('header .logo').getBoundingClientRect();

            const invert = first.top - last.top;
            TweenMax.set(logoClone, {
                y: invert, top: last.top, left: last.left,
            });
        },

        /**
         * (Re)-initializes the page scroll controller.
         *
         * @returns {Void}
         */
        initPageScrollController() {
            if (this.pageScrollController) {
                this.pageScrollController.destroy();
            }

            this.pageScrollController = new ScrollMagic.Controller();

            const revealElements = [].slice.call(document.querySelectorAll('.scroll-reveal'));
            for (let index = 0; index < revealElements.length; index++) {
                const element = revealElements[index];

                new ScrollMagic.Scene({ triggerElement: element, triggerHook: 0.90 })
                    .setClassToggle(element, 'revealed')
                    .addTo(this.pageScrollController);
            }
        },

        /**
         * Handles the default transition for the menu.
         *
         * @param {Event} event
         */
        transitionFromMenu(event) {
            const target = event.currentTarget;
            this.router.transition = this.router.transitions.DefaultFromMenu;

            page(target.href);
        },

        /**
         * Triggers a page transition and uses the data-transition
         * attribute of the event target.
         *
         * @param {Event} event
         */
        pageTransition(event) {
            const transition = event.currentTarget.getAttribute('data-transition');
            this.router.transition = transition;

            page(event.currentTarget.href);
        },

        enter() {
            const onComplete = () => {
                document.querySelector('.slider-holder').classList.remove('animate-in');
                document.querySelector('.start-page.container').style.display = 'none';

                const onCompleteTl = () => {
                    TweenMax.set(['.slider-control .circle'], { clearProps: 'all' });
                    TweenMax.set(['.slider-control.next .arrow'], { clearProps: 'all' });
                    TweenMax.set(['.slider-control.previous .arrow'], { clearProps: 'all' });

                    TweenMax.set('.slide .subline .movement-container', { clearProps: 'all' });
                    TweenMax.set('.slide .scroll .movement-container', { clearProps: 'all' });

                    TweenMax.set('.slide h1 .movement-container', { clearProps: 'all' });

                    TweenMax.set('header .logo', { autoAlpha: 1 });
                    EventBus.$off('window-resize', this.setupLogo);
                    this.showIntroduction = false;
                };

                const tl = new TimelineMax({ onComplete: onCompleteTl });

                tl.to('.slide .subline .movement-container', 0.8, { y: '0%' }, 0.2)
                    .to('.slide .scroll .movement-container', 0.8, { y: '0%' }, 0.2)
                    .set('.slide .scroll', { className: '-=animate-out' }, 0.2)
                    .to('.slide h1 .movement-container', 0.8, { y: '0%' }, 0)
                    .to(['.slider-control .circle'], 0.8, { scale: 1, transformOrigin: 'center center' }, 0.8)
                    .to(['.slider-control.next .arrow'], 0.8, { autoAlpha: 1, y: '0%' }, 0.8)
                    .to(['.slider-control.previous .arrow'], 0.8, { autoAlpha: 1, y: '0%' }, 0.8);
            };

            document.querySelector('.start-page.container').classList.toggle('animate-out');
            TweenMax.to(document.querySelector('.slider-holder .slide .image-holder'), 30, { delay: 0.2, scale: 1.4, ease: ExpoScaleEase.config(1, 1.4) });
            TweenMax.to(document.querySelector('.logo.clone'), 1, {
                y: 0, delay: 0.3, ease: Power2.easeOut, onComplete,
            });

            TweenMax.to('#hamburger', 1, {autoAlpha: 1});
            TweenMax.set('.start-page', { backgroundColor: 'transparent' });
            TweenMax.to('.fullscreen-video-holder', 1, { autoAlpha: 0 });
        },

        /**
         * Toggles navigation state.
         *
         * @param {Event} event
         * @returns {Void}
         */
        toggleNavigation(event) {
            if (this.inMenuTransition) {
                return;
            }

            this.navigationOpen = !this.navigationOpen;
            const hamburger = document.getElementById('hamburger');
            hamburger.classList.toggle('opened');

            this.inMenuTransition = true;

            const onComplete = () => {
                this.inMenuTransition = false;
            };

            const onReverseComplete = () => {
                this.$refs.navigation.style.display = 'none';
                EventBus.$emit('close-menu-complete');
            };

            if (this.navigationOpen) {
                hamburger.classList.remove('hamburger-hover');
                this.headerNavigationState = true;

                // Orange curtain
                this.$refs.navigation.style.display = 'block';

                TweenMax.set('nav li a .number .inner', { y: '100%' });
                TweenMax.set('nav #navigation-info', { autoAlpha: 0 });
                TweenMax.set('nav .lang-switch', { autoAlpha: 0 });
                TweenMax.set('nav li a .line', { scaleX: 0 });

                TweenMax.to('nav .curtain', 0.8, { scaleY: 1, ease: Power3.easeOut });
                TweenMax.staggerTo('nav li a .line', 0.6, {
                    scaleX: '1',
                    delay: 0.2,
                    onCompleteParams: ['{self}'],
                    onComplete(tween) {
                        tween.target.classList.add('css-styles');

                        TweenMax.set(tween.target, { clearProps: 'all' });
                    },
                }, -0.1, onComplete);

                TweenMax.staggerTo('nav li a .movement-container', 0.6, { y: '0%', delay: 0.1, ease: Power3.ease }, 0.1);
                TweenMax.staggerTo('nav li a .number .inner', 0.8, { y: '0%', delay: 0.3 }, -0.1);
                TweenMax.to('nav .lang-switch', 0.6, { autoAlpha: '1', delay: 1 });
                TweenMax.to('nav #navigation-info', 0.6, { autoAlpha: '1', delay: 1 });

                TweenMax.to('#hamburger .upper', 0.6, { y: 9, rotation: 45 });
                TweenMax.to('#hamburger .lower', 0.6, { y: -10, rotation: -45 });
                TweenMax.to('#hamburger .middle', 0.4, { opacity: '0' });
                return;
            }

            TweenMax.to('nav .lang-switch', 0.4, { autoAlpha: '0' });
            TweenMax.to('nav #navigation-info', 0.4, { autoAlpha: '0' });
            TweenMax.staggerTo('nav li a span.movement-container', 0.6, { y: '120%', delay: 0.1, ease: Power3.ease }, -0.1);
            TweenMax.staggerTo('nav li a .number .inner', 0.3, { y: '100%', delay: 0.2 }, -0.1);
            TweenMax.staggerTo('nav li a .line', 0.3, {
                scaleX: '0',
                delay: 0,
                onCompleteParams: ['{self}'],
                onComplete(tween) {
                    tween.target.classList.remove('css-styles');
                },
            }, -0.1, onComplete);

            TweenMax.to('nav .curtain', 0.6, {
                scaleY: 0, delay: 0.1, ease: Power3.easeIn, onComplete: onReverseComplete,
            });

            TweenMax.to('#hamburger .upper', 0.6, { y: 0, rotation: 0 });
            TweenMax.to('#hamburger .lower', 0.6, { y: 0, rotation: 0 });
            TweenMax.to('#hamburger .middle', 0.6, { opacity: '1' });

            TweenMax.delayedCall(0.6, () => {
                hamburger.classList.add('hamburger-hover');
                this.headerNavigationState = false;
            });
        },

        /**
         * Closes the navigation when it's open. Useful when you want a page
         * transition to close the menu at a certain point, without typing
         * this 'if' statement everytime.
         *
         * @returns {Void}
         */
        closeNavigation() {
            if (this.navigationOpen) {
                this.toggleNavigation();
            }
        },

        /**
         * Listen for user scroll event.
         *
         * @returns {Void}
         */
        listenForScroll() {
            this.lethargy = new Lethargy();

            // Cross-browser way to bind to mouse events
            window.addEventListener('mousewheel', this.onUserScroll);
            window.addEventListener('DOMMouseScroll', this.onUserScroll);
            window.addEventListener('wheel', this.onUserScroll);
            window.addEventListener('MozMousePixelScroll', this.onUserScroll);
        },

        /**
         * Removes user scroll listeners.
         *
         * @returns {Void}
         */
        removeScrollListener() {
            this.lethargy = null;

            // Cross-browser way to bind to mouse events
            window.removeEventListener('mousewheel', this.onUserScroll);
            window.removeEventListener('DOMMouseScroll', this.onUserScroll);
            window.removeEventListener('wheel', this.onUserScroll);
            window.removeEventListener('MozMousePixelScroll', this.onUserScroll);
        },

        /**
         * Handles user scroll event.
         *
         * @param {Event}
         * @returns {Void}
         */
        onUserScroll(e) {
            e.preventDefault();
            e.stopPropagation();

            if (!this.lethargy) {
                return;
            }

            const result = this.lethargy.check(e);
            // false means it's not a scroll intent, 1 or -1 means it is
            if (result === -1) {
                EventBus.$emit('user-scroll-down');
            } else if (result === 1) {
                EventBus.$emit('user-scroll-up');
            }
        },

        /**
         * Raised when the client window is resizing. Checks if the
         * application has switched to a mobile breakpoint and
         * act accordingly when a switch has indeed occurred.
         *
         * @return {Void}
         */
        onWindowResize: _debounce(() => {
            EventBus.$emit('window-resize');
        }, 150),

        /**
         * Click event that handles a link and ignores
         * the router.
         *
         * @returns {Void}
         */
        ignoreRouter(event) {
            const href = event.currentTarget.href;

            window.location = href;
        },
    },

    beforeDestroy() {
        EventBus.$off('page-transition-enter');
        EventBus.$off('page-transition-enter-complete');
        EventBus.$off('homepage-scrolldown');

        // Remove window listeners
        window.removeEventListener('resize', this.onWindowResize);
    },
});



// WEBPACK FOOTER //
// ./resources/assets/js/app.js